import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  SyntheticEvent,
} from "react";
import { ArrowIcon, Button } from "@cmsgov/ds-medicare-gov";
import { AppPage, PrintButton } from "../../components";
import AddPrescription from "./AddPrescription";
import EditPrescription from "./EditPrescription";
import ViewPrescriptions from "./ViewPrescriptions";
import { RouteComponentProps } from "react-router";
import routes from "../../app/routes";
import {
  useTranslate,
  sendTealiumEvent,
  addYearInTitle,
  useShouldAddYearToTitle,
} from "../../helpers";
import { AppStore } from "../../app/store";
import { LocationWithNextRouteInState } from "../../@types";
import { hasLoginSession } from "../../helpers/loginHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { saveBeneDrugs } from "../../api";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";
import { hasMailOrderPharmacy } from "../../app/store/AppStore";

export enum PrescriptionsView {
  ADD = "add",
  EDIT = "edit",
  VIEW = "view",
}

const ManagePrescriptionsPage: FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const { state } = useContext(AppStore.AppContext);
  const flags = useFlags();
  const [addedDrug, setAddedDrug] = useState({
    rxcui: "",
    is_generic: false,
    name: "",
  });
  const [mode, setMode] = useState(PrescriptionsView.ADD);
  const [previousMode, setPreviousMode] = useState(PrescriptionsView.ADD);
  const [editPrescriptionIndex, setEditPrescriptionIndex] = useState(-1);

  // heading is the shown on the page, i.e. <h1>.
  const [heading, setHeading] = useState("");
  // title is the page title.
  const [title, setTitle] = useState("");

  const { showYearSwitchBanner, yearInfo } = useShouldAddYearToTitle(
    state.year
  );

  useEffect(() => {
    if (mode === PrescriptionsView.VIEW) {
      const headingValue =
        state.mbpReferrerUri !== ""
          ? t("manage_prescriptions.my_saved_drugs") // standalone drugs page
          : t("prescription.confirm_your_drug_list");

      setHeading(headingValue);
      setTitle(
        state.mbpReferrerUri !== ""
          ? headingValue
          : addYearInTitle({
              title: headingValue,
              yearSwitchBannerShown: showYearSwitchBanner,
              yearInfo,
            })
      );
    } else if (mode === PrescriptionsView.EDIT) {
      const headingValue = t("drug_search.tell_us_about_your_drug");
      setHeading(headingValue);
      setTitle(
        addYearInTitle({
          title: headingValue,
          yearSwitchBannerShown: showYearSwitchBanner,
          yearInfo,
        })
      );
    } else {
      const headingValue = t("drug_search.add_prescription_drug");
      setHeading(headingValue);
      setTitle(
        addYearInTitle({
          title: headingValue,
          yearSwitchBannerShown: showYearSwitchBanner,
          yearInfo,
        })
      );
    }
  }, [mode, state.language, state.mbpReferrerUri, state.year]);

  const onSelectDrug = (addedDrug: {
    rxcui: string;
    is_generic: boolean;
    name: string;
  }): void => {
    setPreviousMode(PrescriptionsView.ADD);
    setMode(PrescriptionsView.EDIT);
    setAddedDrug(addedDrug);
  };

  const goToPlans = (): void => {
    const { search } = props.location;

    props.history.push(`${routes.searchResults}${search}`);
  };

  const goToViewPrescriptions = (): void => {
    setMode(PrescriptionsView.VIEW);
  };

  const goToPharmacySelection = (): void => {
    const shouldSkipToSearchResults = !state.prescriptions.length;
    const specialRoute =
      props.location.state &&
      (props.location.state as LocationWithNextRouteInState).nextRoute;
    let nextRoute: Location | string;

    if (specialRoute) {
      nextRoute = specialRoute as Location;
    } else {
      let redirect = shouldSkipToSearchResults
        ? `${routes.searchResults}`
        : `${routes.pharmacy}`;

      const { search } = props.location;

      if (hasLoginSession()) {
        saveBeneDrugs(state.prescriptions);
        const hasSavedPharmacies =
          state.pharmacies.length > 0 || hasMailOrderPharmacy(state);
        const hasSavedDrugs = state.prescriptions.length > 0;
        if (hasSavedPharmacies && hasSavedDrugs) {
          redirect = routes.managePharmacies;
        }
      }

      nextRoute = `${redirect}${search}`;
    }

    createGa4Event({ event_name: Ga4Event.DRUGS_MANAGED, state, flags });

    props.history.push(nextRoute);
  };

  useEffect(() => {
    if (props.location.pathname === routes.prescriptionsList) {
      setMode(PrescriptionsView.VIEW);
    }
  }, [props.location.pathname]);

  const viewPrescriptions = (
    clearRecentlyAdded: SyntheticEvent | boolean = true
  ): void => {
    if (clearRecentlyAdded) {
      setEditPrescriptionIndex(-1);
    }

    setMode(PrescriptionsView.VIEW);
  };

  const addPrescriptions = (): void => {
    setEditPrescriptionIndex(-1);
    setMode(PrescriptionsView.ADD);
  };

  return (
    <AppPage
      title={title}
      heading={heading}
      headingClass={
        mode === PrescriptionsView.VIEW ? "ds-u-display--inline-block" : ""
      }
      outerClassName="ManagePrescriptions"
      headerExtension={
        state.prescriptions.length && mode === PrescriptionsView.ADD ? (
          <div className="appPage__header-extension ds-u-padding-bottom--0 ds-u-margin-top--1 ds-u-margin-x--2 ds-u-sm-margin-x--7">
            <Button
              variation="transparent"
              onClick={(): void => {
                sendTealiumEvent("Find Plans - Add Drugs", "Back to Drug List");
                setMode(PrescriptionsView.VIEW);
              }}
              className="ds-u-font-weight--normal ds-u-text-decoration--none ds-u-padding--0"
            >
              <ArrowIcon
                direction="left"
                className="ds-u-valign--middle ds-u-margin-right--1"
              />
              {t("prescription.back_to_drug_list")}
            </Button>
          </div>
        ) : null
      }
      subHeading={
        mode === PrescriptionsView.VIEW ? (
          <PrintButton
            tealiumProps={{
              event_action: "Manage Prescriptions",
              event_label: "Print",
            }}
          />
        ) : null
      }
      subHeadingClass="ds-u-md-display--inline-block ds-u-md-float--right"
    >
      {mode === PrescriptionsView.ADD && (
        <AddPrescription
          goToPharmacySelection={goToPharmacySelection}
          onSelectDrug={onSelectDrug}
          goToPlans={goToPlans}
          goToViewPrescriptions={goToViewPrescriptions}
        />
      )}

      {mode === PrescriptionsView.EDIT && addedDrug.rxcui && (
        <EditPrescription
          prescriptionIndex={editPrescriptionIndex}
          previousMode={previousMode}
          resetPrescriptionIndex={(): void => setEditPrescriptionIndex(-1)}
          drug={addedDrug}
          viewList={viewPrescriptions}
          goToPharmacySelection={goToPharmacySelection}
          addPrescriptions={addPrescriptions}
          resetModeOnError={(): void => {
            setEditPrescriptionIndex(-1);
            setMode(PrescriptionsView.ADD);
          }}
        />
      )}

      {mode === PrescriptionsView.VIEW && (
        <ViewPrescriptions
          onEdit={(drug: {
            is_generic: boolean;
            rxcui: string;
            index: number;
            name: string;
          }): void => {
            setEditPrescriptionIndex(drug.index);
            setAddedDrug({
              rxcui: drug.rxcui,
              is_generic: drug.is_generic,
              name: drug.name,
            });
            setPreviousMode(PrescriptionsView.VIEW);
            setMode(PrescriptionsView.EDIT);
          }}
          goToPharmacySelection={goToPharmacySelection}
          addMoreDrugs={addPrescriptions}
          resetPrescriptionIndex={(): void => {
            setEditPrescriptionIndex(-1);
          }}
        />
      )}
    </AppPage>
  );
};

export default ManagePrescriptionsPage;
